/* One browser-local visit shared by homepage, Shop and the rest of the site. */
(() => {
  'use strict';
  if (window.LoveCashmereVisitRotation) return;
  const script = document.currentScript || document.querySelector('script[data-lc-visit-namespace]');
  const namespace = script?.dataset.lcVisitNamespace;
  const prefix = `lc-visit-rotation:v1:${namespace}`;
  const idleMs = 30 * 60 * 1000;
  const throttleMs = 60 * 1000;
  const fallback = Object.freeze({ id: 'default', index: 0, persistent: false });
  const choices = new Map();
  let visit;
  let active;
  let session;
  let history;
  let expired = false;
  let firstEntry = true;
  const explicitEntry = () => {
    try {
      if (window.performance?.getEntriesByType('navigation')[0]?.type !== 'navigate') return false;
      return !document.referrer || new URL(document.referrer).origin !== window.location.origin;
    } catch { return false; }
  };
  const read = (storage, key) => {
    const value = storage.getItem(key);
    return value === null ? null : JSON.parse(value);
  };
  const validIndex = value => Number.isSafeInteger(value) && value >= 0;
  const validActive = value => value && typeof value.id === 'string' && value.id && validIndex(value.index) && Number.isFinite(value.lastActivity);

  function getVisit() {
    if (visit) return visit;
    if (!namespace || script?.dataset.lcVisitEditor === 'true' || window.Shopify?.designMode) return (visit = fallback);
    try {
      session = window.sessionStorage;
      history = window.localStorage;
      const now = Date.now();
      const newEntry = firstEntry && explicitEntry();
      firstEntry = false;
      const saved = read(session, `${prefix}:active`);
      const last = read(history, `${prefix}:history`);
      if ((saved !== null && !validActive(saved)) || (last !== null && !validIndex(last.index))) throw new Error('Invalid visit data');
      if (saved && (!last || saved.index > last.index || saved.lastActivity > now)) throw new Error('Inconsistent visit data');
      if (saved && !newEntry && now - saved.lastActivity < idleMs) {
        active = { ...saved, lastActivity: now };
        session.setItem(`${prefix}:active`, JSON.stringify(active));
      } else {
        const index = last === null ? 0 : last.index + 1;
        if (!validIndex(index)) throw new Error('Invalid visit index');
        active = { id: `${now}:${index}`, index, lastActivity: now };
        // Test both stores before committing a new history index.
        session.setItem(`${prefix}:probe`, '1');
        session.removeItem(`${prefix}:probe`);
        history.setItem(`${prefix}:history`, JSON.stringify({ index }));
        session.setItem(`${prefix}:active`, JSON.stringify(active));
      }
      visit = Object.freeze({ id: active.id, index: active.index, persistent: true });
    } catch {
      visit = fallback;
    }
    return visit;
  }

  function touch(force = false) {
    if (!getVisit().persistent || expired) return;
    const now = Date.now();
    // Resuming an idle page never swaps its visible content. Its next navigation
    // starts the next visit instead of accidentally extending the expired one.
    if (now < active.lastActivity || now - active.lastActivity >= idleMs) {
      expired = true;
      return;
    }
    if (!force && now - active.lastActivity < throttleMs) return;
    try {
      const saved = read(session, `${prefix}:active`);
      // A bfcache page must not overwrite a newer visit established elsewhere.
      if (saved?.id !== active.id) return;
      active.lastActivity = now;
      session.setItem(`${prefix}:active`, JSON.stringify(active));
    } catch {
      expired = true;
    }
  }

  function choose(featureKey, keys, defaultKey) {
    const pool = [...new Set(Array.isArray(keys) ? keys.filter(key => typeof key === 'string' && key) : [])];
    const preferred = pool.includes(defaultKey) ? defaultKey : pool[0];
    if (!preferred || typeof featureKey !== 'string' || !getVisit().persistent) return preferred;
    const signature = JSON.stringify([preferred, ...pool]);
    const feature = `${prefix}:feature:${featureKey}`;
    const cacheKey = `${feature}:${signature}`;
    if (choices.has(cacheKey)) return choices.get(cacheKey);
    let selected = preferred;
    try {
      const saved = read(session, feature);
      const last = read(history, feature);
      const validChoice = value => value && typeof value.signature === 'string' && typeof value.key === 'string';
      if ((saved !== null && (!validChoice(saved) || typeof saved.visitId !== 'string')) || (last !== null && !validChoice(last))) throw new Error('Invalid presentation data');
      if (saved?.signature === signature && saved.visitId === visit.id && pool.includes(saved.key)) {
        selected = saved.key;
      } else {
        if (last?.signature === signature && pool.includes(last.key)) selected = pool[(pool.indexOf(last.key) + 1) % pool.length];
        history.setItem(feature, JSON.stringify({ signature, key: selected }));
        session.setItem(feature, JSON.stringify({ visitId: visit.id, signature, key: selected }));
      }
    } catch {
      selected = preferred;
    }
    choices.set(cacheKey, selected);
    return selected;
  }

  window.LoveCashmereVisitRotation = Object.freeze({ getVisit, choose });
  getVisit();
  document.addEventListener('pointerdown', () => touch(), { passive: true });
  document.addEventListener('keydown', () => touch(), { passive: true });
  window.addEventListener('scroll', () => touch(), { passive: true });
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return touch();
    const previousId = getVisit().id;
    visit = null;
    expired = false;
    choices.clear();
    const current = getVisit();
    if (current.id !== previousId) document.dispatchEvent(new CustomEvent('lc:visit-change', { detail: current }));
  });
  window.addEventListener('pagehide', () => touch(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') touch();
  });
})();
